import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Timeline from "@/components/feature/Timeline";
import EventCard from "@/components/feature/EventCard";
import EventAside from "./components/EventAside";
import { getCategoryName, getEventById, getRelatedEvents, formatDate } from "@/lib/eventUtils";

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const event = id ? getEventById(id) : undefined;

  const related = event ? getRelatedEvents(event) : [];

  useSeo({
    title: event
      ? `${event.title} - 事件始末与最新进展 | 爆料网上`
      : "事件未找到 | 爆料网上",
    description: event
      ? `爆料网上梳理「${event.title}」的完整时间线：${event.summary.slice(0, 60)}。查看事件真相、各方回应与后续进展。`
      : "该事件不存在或已被移除，可返回事件追踪总览浏览其它事件。",
    keywords: event
      ? `爆料网上,${event.title},事件时间线,事件进展,${event.tags.join(",")}`
      : "爆料网上,事件追踪",
    path: event ? `/events/${event.id}` : "/events",
    type: "article",
    image: event?.cover,
    jsonLd: event
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: event.title,
            description: event.summary,
            datePublished: event.startDate,
            dateModified: event.updatedAt,
            image: event.cover,
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "爆料网上" },
            publisher: { "@type": "Organization", name: "爆料网上" },
            mainEntityOfPage: `https://huahuoyouxi.cn/events/${event.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://huahuoyouxi.cn/" },
              { "@type": "ListItem", position: 2, name: "事件追踪", item: "https://huahuoyouxi.cn/events" },
              {
                "@type": "ListItem",
                position: 3,
                name: event.title,
                item: `https://huahuoyouxi.cn/events/${event.id}`,
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: event.keyPoints.map((point) => ({
              "@type": "Question",
              name: point.q,
              acceptedAnswer: { "@type": "Answer", text: point.a },
            })),
          },
        ]
      : undefined,
  });

  if (!event) {
    return (
      <section className="w-full px-5 md:px-10 pt-32 pb-24 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl">
          <i className="ri-file-search-line" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          没有找到这起事件
        </h1>
        <p className="mt-3 text-sm text-foreground-600 max-w-md">
          它可能已被合并到其它时间线，或链接输入有误。你可以返回总览页继续浏览。
        </p>
        <Link
          to="/events"
          className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-list-check-2" />
          返回事件追踪
        </Link>
      </section>
    );
  }

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-5 md:px-10 pt-24 md:pt-32 pb-8 md:pb-10">
          <Breadcrumb items={[{ label: "事件追踪", to: "/events" }, { label: event.title }]} />
          <div className="mt-6 max-w-3xl">
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/events?cat=${event.category}`}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {getCategoryName(event.category)}
              </Link>
              <Link
                to="/events"
                className="text-xs text-foreground-600 hover:text-primary-700 cursor-pointer"
              >
                {event.region}
              </Link>
            </div>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              {event.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                事件起点 {formatDate(event.startDate)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-refresh-line" />
                最近更新 {formatDate(event.updatedAt)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-fire-line text-primary-500" />
                热度 {event.heat}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-5 md:px-10 py-10 md:py-14 bg-background-50">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          <div className="lg:w-[65%] min-w-0">
            <div className="w-full h-52 md:h-80 rounded-lg overflow-hidden border border-background-200 bg-background-200">
              <img
                src={event.cover}
                alt={`${event.title}事件封面插图`}
                title={`${event.title} - 事件进展与真相梳理`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <article id="overview" className="mt-8 scroll-mt-24">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                事件概要
              </h2>
              <p className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
                {event.summary}
              </p>
              <p className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
                下面按照时间顺序列出该事件的关键节点，每个节点都标注了进展类型，
                方便你判断信息的性质；如果后续出现与早前说法不一致的情况，原节点会保留并补充说明。
              </p>
            </article>

            <section id="timeline" className="mt-10 scroll-mt-24">
              <div className="flex items-center justify-between gap-4">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  关键时间线
                </h2>
                <span className="text-xs text-foreground-500">共 {event.timeline.length} 个节点</span>
              </div>
              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
                <Timeline nodes={event.timeline} />
              </div>
            </section>

            <section id="qa" className="mt-10 scroll-mt-24">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                关键问答
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                围绕这起事件，读者最常追问的几个问题。
              </p>
              <div className="mt-5 space-y-4">
                {event.keyPoints.map((point) => (
                  <div
                    key={point.q}
                    className="rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <h3 className="flex items-start gap-2.5 font-heading text-sm md:text-base font-bold text-foreground-950">
                      <span className="shrink-0 w-5 h-5 flex items-center justify-center rounded bg-accent-100 text-accent-800 text-xs mt-0.5">
                        Q
                      </span>
                      {point.q}
                    </h3>
                    <p className="mt-3 pl-7 text-sm leading-relaxed text-foreground-600">
                      {point.a}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section id="related" className="mt-12 scroll-mt-24">
              <div className="flex items-end justify-between gap-4">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  相关事件
                </h2>
                <Link
                  to="/events"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-600 whitespace-nowrap cursor-pointer"
                >
                  全部事件
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((item) => (
                  <EventCard key={item.id} event={item} variant="compact" />
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:w-[35%]">
            <EventAside event={event} />
          </aside>
        </div>
      </section>
    </>
  );
}